<template>
  <div class="message-container">
    <div class="message-header">
      <h2 class="page-title">
        <i class="el-icon-message"></i>
        消息中心
      </h2>
      <div class="header-actions">
        <el-button size="small" @click="markAllAsRead" :disabled="unreadCount === 0">
          <i class="el-icon-circle-check"></i> 一键已读
        </el-button>
        <el-button size="small" @click="refreshList">
          <i class="el-icon-refresh"></i> 刷新
        </el-button>
      </div>
    </div>

    <div class="message-tabs">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane label="全部消息" name="all">
          <span slot="label">
            <i class="el-icon-chat-dot-square"></i>
            全部消息
            <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="tab-badge"/>
          </span>
        </el-tab-pane>
        <el-tab-pane label="未读消息" name="unread">
          <span slot="label">
            <i class="el-icon-bell"></i>
            未读消息
            <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="tab-badge"/>
          </span>
        </el-tab-pane>
        <el-tab-pane label="系统消息" name="system">
          <span slot="label">
            <i class="el-icon-setting"></i>
            系统消息
          </span>
        </el-tab-pane>
        <el-tab-pane label="用户消息" name="user">
          <span slot="label">
            <i class="el-icon-user"></i>
            用户消息
          </span>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="message-list" v-loading="loading">
      <div class="message-item" v-for="msg in messageList" :key="msg.id" :class="{ 'unread': msg.isRead === 0 }">
        <div class="message-avatar">
          <el-avatar :size="40" :src="msg.senderIcon || defaultAvatar" icon="el-icon-user"></el-avatar>
        </div>
        <div class="message-content">
          <div class="message-header-inner">
            <span class="sender-name">{{ msg.senderName || '系统' }}</span>
            <span class="message-time">{{ formatDate(msg.createTime) }}</span>
            <el-tag v-if="msg.type === 'system'" size="mini" type="warning">系统</el-tag>
            <el-tag v-else size="mini" type="primary">用户</el-tag>
          </div>
          <div class="message-title" v-if="msg.title">{{ msg.title }}</div>
          <div class="message-text">{{ msg.content }}</div>
          <div class="message-footer">
            <el-button 
              v-if="msg.isRead === 0" 
              type="text" 
              size="small" 
              @click="markAsRead(msg.id)"
            >
              标记已读
            </el-button>
            <el-button 
              type="text" 
              size="small" 
              @click="deleteMessage(msg.id)"
            >
              删除
            </el-button>
            <el-button 
              v-if="msg.relatedType === 'doc' && msg.relatedId" 
              type="text" 
              size="small"
              @click="goToArticle(msg.relatedId)"
            >
              查看详情
            </el-button>
          </div>
        </div>
      </div>

      <el-empty v-if="messageList.length === 0 && !loading" description="暂无消息">
        <el-button type="primary" @click="goHome">去首页逛逛</el-button>
      </el-empty>
    </div>

    <div class="message-pagination" v-if="total > 0">
      <el-pagination
        @current-change="handlePageChange"
        :current-page="pageNum"
        :page-size="pageSize"
        layout="total, prev, pager, next"
        :total="total"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Message',
  data() {
    return {
      activeTab: 'all',
      messageList: [],
      loading: false,
      pageNum: 1,
      pageSize: 20,
      total: 0,
      unreadCount: 0,
      defaultAvatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
    };
  },
  created() {
    this.getMessageList();
    this.getUnreadCount();
  },
  methods: {
    async getMessageList() {
      this.loading = true;
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          token: loginResult.token || '',
          userId: loginResult.userId || '',
          userName: loginResult.userName || '',
          ip: '',
          source: 'web'
        };

        if (this.activeTab === 'unread') {
          params.isRead = 0;
        } else if (this.activeTab === 'system') {
          params.type = 'system';
        } else if (this.activeTab === 'user') {
          params.type = 'user';
        }

        const response = await this.$axios({
          method: 'post',
          url: '/message/list',
          data: params
        });

        if (response.code === '200' || response.code === 200) {
          this.messageList = response.data || [];
          this.total = this.messageList.length;
        } else {
          this.$message.error(response.msg || '获取消息失败');
        }
      } catch (error) {
        console.error('获取消息失败:', error);
        this.$message.error('获取消息失败');
      } finally {
        this.loading = false;
      }
    },

    async getUnreadCount() {
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        const response = await this.$axios({
          method: 'post',
          url: '/message/unreadCount',
          data: {
            token: loginResult.token || '',
            userId: loginResult.userId || '',
            userName: loginResult.userName || '',
            ip: '',
            source: 'web'
          }
        });

        if (response.code === '200' || response.code === 200) {
          this.unreadCount = response.data || 0;
        }
      } catch (error) {
        console.error('获取未读数失败:', error);
      }
    },

    handleTabClick() {
      this.pageNum = 1;
      this.getMessageList();
    },

    handlePageChange(page) {
      this.pageNum = page;
      this.getMessageList();
    },

    async markAsRead(messageId) {
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        const response = await this.$axios({
          method: 'post',
          url: '/message/markAsRead',
          data: {
            messageIds: [messageId],
            token: loginResult.token || '',
            userId: loginResult.userId || '',
            userName: loginResult.userName || '',
            ip: '',
            source: 'web'
          }
        });

        if (response.code === '200' || response.code === 200) {
          this.$message.success('标记成功');
          this.getMessageList();
          this.getUnreadCount();
          // 通知顶部导航栏更新未读数
          this.$emit('updateUnreadCount', this.unreadCount - 1);
        } else {
          this.$message.error(response.msg || '操作失败');
        }
      } catch (error) {
        console.error('标记已读失败:', error);
        this.$message.error('操作失败');
      }
    },

    async markAllAsRead() {
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        const response = await this.$axios({
          method: 'post',
          url: '/message/markAllAsRead',
          data: {
            token: loginResult.token || '',
            userId: loginResult.userId || '',
            userName: loginResult.userName || '',
            ip: '',
            source: 'web'
          }
        });

        if (response.code === '200' || response.code === 200) {
          this.$message.success('一键已读成功');
          this.getMessageList();
          this.getUnreadCount();
          // 通知顶部导航栏更新未读数
          this.$emit('updateUnreadCount', 0);
        } else {
          this.$message.error(response.msg || '操作失败');
        }
      } catch (error) {
        console.error('一键已读失败:', error);
        this.$message.error('操作失败');
      }
    },

    async deleteMessage(messageId) {
      this.$confirm('确认删除该消息？', '提示', { type: 'warning' })
        .then(async () => {
          try {
            const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
            const response = await this.$axios({
              method: 'post',
              url: '/message/delete',
              data: {
                messageIds: [messageId],
                token: loginResult.token || '',
                userId: loginResult.userId || '',
                userName: loginResult.userName || '',
                ip: '',
                source: 'web'
              }
            });

            if (response.code === '200' || response.code === 200) {
              this.$message.success('删除成功');
              this.getMessageList();
              this.getUnreadCount();
            } else {
              this.$message.error(response.msg || '删除失败');
            }
          } catch (error) {
            console.error('删除失败:', error);
            this.$message.error('删除失败');
          }
        })
        .catch(() => {});
    },

    refreshList() {
      this.getMessageList();
      this.getUnreadCount();
    },

    goToArticle(id) {
      this.$router.push({ name: 'Content', params: { id } });
    },

    goHome() {
      this.$router.push({ name: 'Home' });
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return '刚刚';
      if (diffMins < 60) return `${diffMins}分钟前`;
      if (diffHours < 24) return `${diffHours}小时前`;
      if (diffDays < 30) return `${diffDays}天前`;

      return date.toLocaleDateString('zh-CN');
    }
  }
};
</script>

<style lang="less" scoped>
.message-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  margin-top: 64px;
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #333;

    i {
      margin-right: 10px;
      color: #409EFF;
    }
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }
}

.message-tabs {
  margin-bottom: 20px;

  ::v-deep .el-tabs__header {
    background: #fff;
    padding: 0 20px;
    border-radius: 12px 12px 0 0;
  }

  .tab-badge {
    margin-left: 8px;
  }
}

.message-list {
  background: #fff;
  border-radius: 0 0 12px 12px;
  padding: 20px;
  min-height: 400px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

  .message-item {
    display: flex;
    padding: 20px;
    border-bottom: 1px solid #f0f0f0;
    transition: all 0.3s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8f9fa;
    }

    &.unread {
      background: #f0f7ff;
      border-left: 3px solid #409EFF;
      padding-left: 17px;
    }

    .message-avatar {
      margin-right: 16px;
      flex-shrink: 0;
    }

    .message-content {
      flex: 1;
      min-width: 0;

      .message-header-inner {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;

        .sender-name {
          font-weight: 600;
          color: #333;
          font-size: 15px;
        }

        .message-time {
          color: #999;
          font-size: 13px;
        }
      }

      .message-title {
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
        font-size: 15px;
      }

      .message-text {
        color: #666;
        line-height: 1.6;
        margin-bottom: 12px;
        font-size: 14px;
      }

      .message-footer {
        display: flex;
        gap: 12px;

        .el-button {
          padding: 4px 0;
          font-size: 13px;
        }
      }
    }
  }
}

.message-pagination {
  margin-top: 24px;
  text-align: center;
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
</style>
